import {useEffect, Suspense, lazy} from 'react'
import { Provider } from 'react-redux'
import {HashRouter, Routes, Route, useNavigate, useLocation} from 'react-router-dom';
import navigationService from '../common/navigationService';
import { AuthProvider, LoginInputProvider } from '../auth';
import LayoutContainer from './LayoutContainer'
import * as constants from '../common/constants'
import { PageContainer, PageMessage, LoaderProvider } from '../components'

//Login Pages
import PageLogin from '../loginpages/PageLogin'
import PageLogout from '../loginpages/PageLogout'
import PageWebLogin from '../loginpages/PageWebLogin'
import PageMemberLogin from '../loginpages/PageMemberLogin'
import PageAuthenticated from '../loginpages/PageAuthenticated'
import PagePasswordLessSamlSubmit from '../loginpages/PagePasswordLessSamlSubmit'
import PageNotAuthorized from '../loginpages/PageNotAuthorized'
import PageMaxSessionsExceed from '../loginpages/PageMaxSessionsExceed'
import PageExistingSessions from '../loginpages/PageExistingSessions'
import PageEula from '../loginpages/PageEula'
import PageConfirmContact from '../loginpages/PageConfirmContact'
import PageProfile from '../loginpages/PageProfile'
import PageUserInfo from '../loginpages/PageUserInfo'
import PageChangeGlobalEmail from '../loginpages/PageChangeGlobalEmail'
import PageSamlMessage from '../loginpages/PageSamlMessage'
import PageWsFedMessage from '../loginpages/PageWsFedMessage'
import PageSamlTfa from '../loginpages/PageSamlTfa'
import PageSamlChooseApp from '../loginpages/PageSamlChooseApp'

//Password Pages
import PageForgotPassword from '../passwordpages/PageForgotPassword'
import PageChangePassword from '../passwordpages/PageChangePassword'

//Global ID Pages
import PageRequestGlobalId from '../globalidpages/PageRequestGlobalId'
import PageRegisterGlobalIdWithCompany from '../globalidpages/PageRegisterGlobalIdWithCompany'
import PageConfirmUserEmail from '../globalidpages/PageConfirmUserEmail'

//TFA Pages
import PageTfaRegister from '../tfapages/PageTfaRegister'
import PageTfaEmailRegister from '../tfapages/PageTfaEmailRegister'
import PageTfaRegistrationDone from '../tfapages/PageTfaRegistrationDone'
import PageTfaHelp from '../tfapages/PageTfaHelp'
import PageEmailPasscode from '../tfapages/PageEmailPasscode'
import PageUpdateTfaLogin from '../tfapages/PageUpdateTfaLogin'
import PageRegisterLogin from '../tfapages/PageRegisterLogin'
import PageAppUserLoginTfa from "../loginpages/PageAppUserLoginTfa";
import PageWebAuthnRegister from "../tfapages/PageWebAuthnRegister";
import PageWebAuthnDevices from "../tfapages/PageWebAuthnDevices";
import PageWebAuthnAuth from "../tfapages/PageWebAuthnAuth";

const RegistrationHome = lazy(() => import('../registration/RegistrationHome'));

// Component to initialize navigation service and log route changes
function NavigationServiceInitializer() {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        navigationService.setNavigate(navigate);
    }, [navigate]);

    useEffect(() => {
        console.log('[SSO Route]', location.pathname, location.state || '');
    }, [location]);

    return null;
}

const RouteProvider = (props) => <Provider {...props}>
    
    <HashRouter>
        <LoaderProvider>
        <AuthProvider>
        <LoginInputProvider>
        <NavigationServiceInitializer />
        <Routes>
            <Route path={"/"} element={<LayoutContainer />}>
                <Route index element={<PageLogin />} />
                <Route path="/index.html" element={<PageLogin />} />

                { /* Login Pages */}
                <Route path={constants.ROUTE_PAGE_LOGIN} element={<PageLogin />}/>
                <Route path={constants.ROUTE_PAGE_WEB_LOGIN} element={<PageWebLogin/>}/>
                <Route path={constants.ROUTE_PAGE_MEMBER_LOGIN} element={<PageMemberLogin/>} />
                <Route path={constants.ROUTE_PAGE_AUTHENTICATED} element={<PageAuthenticated />}/>
                <Route path={constants.ROUTE_PAGE_PASSWORD_LESS_SAML_SUBMIT} element={<PagePasswordLessSamlSubmit/>}/>
                <Route path={constants.ROUTE_PAGE_USERINFO} element={<PageUserInfo/>}/>
                <Route path={constants.ROUTE_PAGE_WEBAUTHNREG} element={<PageContainer><PageWebAuthnRegister/></PageContainer>}/>
                <Route path={constants.ROUTE_PAGE_WEBAUTHNAUTH} element={<PageWebAuthnAuth/>}/>
                <Route path={constants.ROUTE_PAGE_WEBAUTHNDEVICES} element={<PageWebAuthnDevices/>}/>
                <Route path={constants.ROUTE_PAGE_NOT_AUTHORIZED} element={<PageNotAuthorized/>} />
                <Route path={constants.ROUTE_PAGE_MAX_SESSIONS_EXCEED} element={<PageMaxSessionsExceed/>} />
                <Route path={constants.ROUTE_PAGE_USER_HAS_EXISTING_SESSIONS} element={<PageExistingSessions/>} />
                <Route path={constants.ROUTE_PAGE_EULA} element={<PageEula/>} />
                <Route path={constants.ROUTE_PAGE_CONFIRM_CONTACT} element={<PageConfirmContact/>} />
                <Route path={constants.ROUTE_PAGE_PROFILE} element={<PageProfile/>} />
                <Route path={constants.ROUTE_PAGE_SAML_MESSAGE} element={<PageSamlMessage/>} />
                <Route path={constants.ROUTE_PAGE_WSFED_MESSAGE} element={<PageWsFedMessage/>} />
                <Route path={constants.ROUTE_PAGE_SAML_TFA} element={<PageSamlTfa/>} />
                <Route path={constants.ROUTE_PAGE_APP_USER_LOGIN_TFA} element={<PageAppUserLoginTfa/>} />
                <Route path={constants.ROUTE_PAGE_SAML_CHOOSE_APP} element={<PageSamlChooseApp/>} />
                <Route path={constants.ROUTE_PAGE_LOGOUT} element={<PageLogout/>} />
                <Route path={constants.ROUTE_PAGE_CHANGE_EMAIL} element={<PageChangeGlobalEmail/>} />

                { /* Password Pages */}
                <Route path={constants.ROUTE_PAGE_FORGOT_PASSWORD} element={<PageForgotPassword/>} />
                <Route path={constants.ROUTE_PAGE_CHANGE_PASSWORD} element={<PageChangePassword/>} />

                { /* TFA Pages */}
                <Route path={constants.ROUTE_PAGE_TFA_REGISTER} element={<PageTfaRegister/>} />
                <Route path={constants.ROUTE_PAGE_TFA_EMAIL_REGISTER} element={<PageTfaEmailRegister/>} />
                <Route path={constants.ROUTE_PAGE_TFA_REGISTRATION_DONE} element={<PageTfaRegistrationDone/>} />
                <Route path={constants.ROUTE_PAGE_UPDATE_TFA_LOGIN} element={<PageUpdateTfaLogin/>} />
                <Route path={constants.ROUTE_PAGE_REGISTER_LOGIN} element={<PageRegisterLogin/>} />
                <Route path={constants.ROUTE_PAGE_EMAIL_PASSCODE} element={<PageEmailPasscode/>} />
                <Route path={constants.ROUTE_PAGE_TFA_HELP} element={<PageTfaHelp/>} />

                { /* Global ID Pages */}
                <Route path={constants.ROUTE_PAGE_REQUEST_GLOBAL_ID} element={<PageRequestGlobalId/>} />
                <Route path={constants.ROUTE_PAGE_REGISTER_GLOBAL_ID_WITH_COMPANY} element={<PageRegisterGlobalIdWithCompany/>} />
                <Route path={constants.ROUTE_PAGE_CONFIRM_USER_EMAIL} element={<PageConfirmUserEmail/>} />

                { /* Servlets */}
                <Route path={constants.ROUTE_FORGOT_PASSWORD_SERVLET} element={<PageForgotPassword/>} />
                <Route path={constants.ROUTE_CHANGE_PASSWORD_SERVLET} element={<PageChangePassword/>} />
                <Route path={constants.ROUTE_REQUEST_GLOBAL_ID_SERVLET} element={<PageRequestGlobalId/>} />
                <Route path={constants.ROUTE_REGISTER_GLOBAL_ID_WITH_COMPANY_SERVLET} element={<PageRegisterGlobalIdWithCompany/>} />
                <Route path={constants.ROUTE_CONFIRM_USER_EMAIL_SERVLET} element={<PageConfirmUserEmail/>} />
                <Route path={constants.ROUTE_APP_USER_LOGIN_SERVLET} element={<PageLogin/>} />
                <Route path={constants.ROUTE_APP_USER_RELOGIN_SERVLET} element={<PageLogin/>} />
                <Route path={constants.ROUTE_MEMBER_LOGIN_SERVLET} element={<PageWebLogin/>} />
                <Route path={constants.ROUTE_MEMBER_APP_LOGIN_SERVLET} element={<PageUserInfo/>} />

                { /* Common Components */}
                <Route path={constants.ROUTE_PAGE_MESSAGE} element={<PageMessage/>} />
                <Route path={"/registration"} element={
                    <Suspense fallback={<div>Loading...</div>}>
                        <RegistrationHome />
                    </Suspense>
                } />
                <Route path={constants.ROUTE_PAGE_UPDATE_REGISTRATION} element={
                    <Suspense fallback={<div>Loading...</div>}>
                        <RegistrationHome />
                    </Suspense>
                } />
                <Route path="*" element={<PageLogin />} />
            </Route>
        </Routes>
        </LoginInputProvider>
        </AuthProvider>
        </LoaderProvider>
    </HashRouter>
    
</Provider>;

export default RouteProvider
